import * as THREE from 'three';
import { Effect, EffectAttribute, BlendFunction } from 'postprocessing';

// ── AERIAL PERSPECTIVE ───────────────────────────────────────────────────────
//
// The haze between you and everything far away — what makes a city look a mile
// deep instead of a model on a table. Done in screen space from the depth
// buffer rather than with three's per-material fog, for three reasons:
//
//   · HEIGHT. Real haze pools low and thins with altitude, so a tower's base is
//     lost in it while its crown stands clear against the sky. three's fog is a
//     function of distance only.
//   · THE SUN. Looking toward the sun, haze glows with its colour — the golden
//     wash down an avenue at sunset. Looking away it stays cool. One shared
//     colour cannot do that.
//   · EVERYTHING. The skyline ring had `fog = false` because three's fog went
//     fully opaque at 260 units and would have erased it. A per-pixel haze over
//     the finished image treats every surface the same, so the skyline gets the
//     depth it was missing instead of being cut out of the atmosphere.
//
// Sky pixels (nothing written to depth) are left alone — the sky shader carries
// its own horizon haze, in the same colour, so the two meet without a seam.
const ATMOS_FRAG = /* glsl */`
uniform mat4 uInvProj;
uniform mat4 uCamWorld;
uniform vec3 uCamPos;
uniform vec3 uFogColor;
uniform vec3 uSunDir;
uniform vec3 uSunGlow;
uniform float uHaze;
uniform float uHazeFalloff;
uniform float uMaxFog;

void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
  if (depth >= 0.99999) { outputColor = inputColor; return; }
  vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
  vec4 vp = uInvProj * ndc;
  vp /= vp.w;
  vec3 wp = (uCamWorld * vec4(vp.xyz, 1.0)).xyz;
  vec3 ray = wp - uCamPos;
  float d = length(ray);
  vec3 rd = ray / max(d, 1e-4);

  // Optical depth through an exponential height fog, integrated exactly along
  // the ray: density(y) = haze * exp(-y / H).
  float b = uHazeFalloff;
  float y0 = max(uCamPos.y, 0.0);
  float y1 = max(wp.y, 0.0);
  float dy = y1 - y0;
  float tau;
  if (abs(dy) < 0.05) tau = uHaze * exp(-b * y0) * d;
  else tau = uHaze * d * (exp(-b * y0) - exp(-b * y1)) / (b * dy);
  float f = min(1.0 - exp(-tau), uMaxFog);

  // In-scatter: toward the sun the haze takes the sun's colour.
  float mu = max(dot(rd, uSunDir), 0.0);
  vec3 scatter = uFogColor + uSunGlow * (pow(mu, 6.0) * 0.55 + pow(mu, 32.0) * 0.6);
  outputColor = vec4(mix(inputColor.rgb, scatter, f), inputColor.a);
}
`;

export class AtmosphereEffect extends Effect {
  constructor() {
    super('AtmosphereEffect', ATMOS_FRAG, {
      attributes: EffectAttribute.DEPTH,
      blendFunction: BlendFunction.SRC,
      uniforms: new Map([
        ['uInvProj', new THREE.Uniform(new THREE.Matrix4())],
        ['uCamWorld', new THREE.Uniform(new THREE.Matrix4())],
        ['uCamPos', new THREE.Uniform(new THREE.Vector3())],
        ['uFogColor', new THREE.Uniform(new THREE.Color())],
        ['uSunDir', new THREE.Uniform(new THREE.Vector3(0, 1, 0))],
        ['uSunGlow', new THREE.Uniform(new THREE.Color())],
        ['uHaze', new THREE.Uniform(0.002)],
        ['uHazeFalloff', new THREE.Uniform(1 / 45)],
        ['uMaxFog', new THREE.Uniform(0.92)],
      ]),
    });
  }

  sync(camera, sky, skyU) {
    const u = this.uniforms;
    u.get('uInvProj').value.copy(camera.projectionMatrixInverse);
    u.get('uCamWorld').value.copy(camera.matrixWorld);
    u.get('uCamPos').value.setFromMatrixPosition(camera.matrixWorld);
    u.get('uFogColor').value.copy(sky.fogColor);
    u.get('uSunDir').value.copy(sky.sunDir);
    u.get('uSunGlow').value.copy(skyU.uSunColor.value).multiplyScalar(0.55 * skyU.uSunVis.value);
    u.get('uHaze').value = sky.haze;
  }
}

// ── THE GRADE ────────────────────────────────────────────────────────────────
//
// Exposure → filmic tone curve → colour grade → vignette → grain, in one pass.
//
// The tone curve is three's own ACES fit, with the same exposure semantics, so
// a value tuned against the old renderer means the same thing here. The grade
// after it is the part that makes a frame look DIRECTED rather than rendered:
//
//   · split toning — a cool tint pushed into the shadows, a warm one into the
//     highlights. The single most recognisable trait of a modern open-world
//     game's look (and of the LA films those games borrow from). Both tints come
//     from the hour: teal/orange at night, violet/peach at golden hour, nearly
//     neutral at noon.
//   · saturation and contrast around mid-grey, from the hour as well.
//   · a soft vignette and a whisper of film grain, which kill the clean CG
//     flatness without anybody noticing why.
const GRADE_FRAG = /* glsl */`
uniform float uExposure;
uniform vec3 uShadowTint;
uniform vec3 uHighTint;
uniform float uSplit;
uniform float uSaturation;
uniform float uContrast;
uniform float uVignette;
uniform float uGrain;
uniform float uTime;

vec3 RRTAndODTFit(vec3 v) {
  vec3 a = v * (v + 0.0245786) - 0.000090537;
  vec3 b = v * (0.983729 * v + 0.4329510) + 0.238081;
  return a / b;
}
vec3 acesFilmic(vec3 color) {
  const mat3 ACESInputMat = mat3(vec3(0.59719, 0.07600, 0.02840), vec3(0.35458, 0.90834, 0.13383), vec3(0.04823, 0.01566, 0.83777));
  const mat3 ACESOutputMat = mat3(vec3(1.60475, -0.10208, -0.00327), vec3(-0.53108, 1.10813, -0.07276), vec3(-0.07367, -0.00605, 1.07602));
  color *= uExposure / 0.6;
  color = ACESInputMat * color;
  color = RRTAndODTFit(color);
  color = ACESOutputMat * color;
  return clamp(color, 0.0, 1.0);
}
float hashG(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = acesFilmic(inputColor.rgb);
  // Grade in a perceptual-ish space (gamma 2.2) so "shadows" means what the eye
  // calls shadows; back to linear afterwards for the output encode.
  c = pow(c, vec3(1.0 / 2.2));
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  // Split toning.
  float shadowW = (1.0 - smoothstep(0.0, 0.55, l)) * uSplit;
  float highW = smoothstep(0.45, 1.0, l) * uSplit;
  c = mix(c, c * (uShadowTint * 2.0), shadowW * 0.16);
  c = mix(c, c * (uHighTint * 1.6), highW * 0.14);
  // Contrast about mid-grey, then saturation.
  c = (c - 0.5) * uContrast + 0.5;
  l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, uSaturation);
  // Vignette.
  vec2 q = uv - 0.5;
  float v = 1.0 - dot(q, q) * uVignette;
  c *= clamp(v, 0.0, 1.0);
  // Grain — luminance-weighted so blacks stay black.
  float g = hashG(uv * 1024.0 + fract(uTime * 7.31) * 97.0) - 0.5;
  c += g * uGrain * (0.35 + 0.65 * (1.0 - l));
  c = clamp(c, 0.0, 1.0);
  outputColor = vec4(pow(c, vec3(2.2)), inputColor.a);
}
`;

const NEUTRAL = new THREE.Color(0.5, 0.5, 0.5);

export class GradeEffect extends Effect {
  constructor() {
    super('GradeEffect', GRADE_FRAG, {
      blendFunction: BlendFunction.SRC,
      uniforms: new Map([
        ['uExposure', new THREE.Uniform(1)],
        ['uShadowTint', new THREE.Uniform(new THREE.Color(0.5, 0.5, 0.5))],
        ['uHighTint', new THREE.Uniform(new THREE.Color(0.62, 0.62, 0.62))],
        ['uSplit', new THREE.Uniform(1)],
        ['uSaturation', new THREE.Uniform(1.08)],
        ['uContrast', new THREE.Uniform(1.08)],
        ['uVignette', new THREE.Uniform(0.55)],
        ['uGrain', new THREE.Uniform(0.022)],
        ['uTime', new THREE.Uniform(0)],
      ]),
    });
  }

  sync(sky, t, graded = true) {
    const u = this.uniforms;
    u.get('uExposure').value = sky.exposure;
    // Tints are stored as "the colour to lean toward"; normalise to mean 0.5 so a
    // tint shifts hue without changing brightness.
    // Half-way back toward neutral as well: the keyframe colours name a
    // direction, and a grade that went all the way there would repaint the frame.
    const norm = (src, dst) => {
      const m = (src.r + src.g + src.b) / 3 || 1;
      dst.setRGB((src.r / m) * 0.5, (src.g / m) * 0.5, (src.b / m) * 0.5).lerp(NEUTRAL, 0.45);
    };
    norm(sky.gradeShadow, u.get('uShadowTint').value);
    norm(sky.gradeHigh, u.get('uHighTint').value);
    u.get('uHighTint').value.multiplyScalar(1.25);
    u.get('uSplit').value = graded ? 1 : 0;
    u.get('uSaturation').value = graded ? sky.saturation : 1;
    u.get('uContrast').value = graded ? sky.contrast : 1;
    u.get('uVignette').value = graded ? 0.55 : 0;
    u.get('uGrain').value = graded ? 0.02 : 0;
    u.get('uTime').value = t;
  }
}
